import React, { useState, useRef, useEffect } from "react";
import { toPng } from "html-to-image";
import { FaShieldAlt, FaCrown } from "react-icons/fa";

const DEFAULT_BG =
  "https://images.unsplash.com/photo-1518605368461-1ee7c532066d?q=80&w=1080&auto=format&fit=crop";

const ExportChampionModal = ({
  isOpen,
  onClose,
  allSubcategories,
  tournamentId,
  token,
}) => {
  const exportRef = useRef(null);

  // Logos y fondo (se guardan en localStorage)
  const [topLogo, setTopLogo] = useState(
    localStorage.getItem("exportTopLogo") || "",
  );
  const [bottomLogo, setBottomLogo] = useState(
    localStorage.getItem("exportBottomLogo") || "",
  );
  const [bgImage, setBgImage] = useState(
    localStorage.getItem("exportBgImage") || DEFAULT_BG,
  );
  const [isExporting, setIsExporting] = useState(false);

  // Categoría y equipos
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  // Cargar equipos cada vez que cambia la categoría seleccionada
  useEffect(() => {
    if (!selectedCategoryId) {
      setTeams([]);
      setSelectedTeamId("");
      return;
    }
    const fetchTeams = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/tournaments/${tournamentId}/teams?subcategoryId=${selectedCategoryId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await res.json();
        setTeams(data || []);
        setSelectedTeamId(""); // resetear selección
      } catch (error) {
        console.error("Error cargando equipos:", error);
        setTeams([]);
      }
    };
    fetchTeams();
  }, [selectedCategoryId, tournamentId, token]);

  // Objeto de categoría seleccionada para el subtítulo
  const selectedCategory = allSubcategories.find(
    (cat) => cat.id == selectedCategoryId,
  );
  const selectedTeam = teams.find((t) => t.team_id == selectedTeamId);

  const getSubtitle = () => {
    if (!selectedCategory) return "";
    let parts = [];
    if (selectedCategory.subcategory_number)
      parts.push(`Categoría ${selectedCategory.subcategory_number}`);
    if (selectedCategory.group_name) parts.push(selectedCategory.group_name);
    if (parts.length === 0 && selectedCategory.name)
      parts.push(selectedCategory.name);
    return parts.join(" - ");
  };

  const subtitle = getSubtitle();

  const handleImageUpload = (e, setFunction, storageKey) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500000) {
        alert(
          "La imagen es muy grande. Por favor sube una imagen menor a 1.5MB",
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFunction(reader.result);
        localStorage.setItem(storageKey, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExport = async () => {
    if (!exportRef.current) return;
    if (!selectedTeam) {
      alert("Selecciona un equipo campeón");
      return;
    }
    setIsExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "transparent",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `Campeon_${selectedTeam.team_name || "Torneo"}.png`;
      link.click();
    } catch (error) {
      alert("Hubo un error al generar la imagen.");
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl">
        {/* Panel de configuración */}
        <div className="w-full md:w-1/3 p-6 bg-gray-50 overflow-y-auto border-r border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Exportar Campeón
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-500 font-bold text-xl"
            >
              &times;
            </button>
          </div>

          {/* Selector de categoría */}
          <div className="space-y-2 mb-6">
            <label className="block text-sm font-semibold text-gray-700">
              Selecciona la categoría
            </label>
            <select
              className="w-full p-2 rounded border border-gray-300 text-gray-900"
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
            >
              <option value="">-- Elegir categoría --</option>
              {allSubcategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.subcategory_number
                    ? `Sub ${cat.subcategory_number} - ${cat.group_name || cat.name}`
                    : cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de equipo (depende de la categoría) */}
          <div className="space-y-2 mb-6">
            <label className="block text-sm font-semibold text-gray-700">
              Selecciona al campeón
            </label>
            <select
              className="w-full p-2 rounded border border-gray-300 text-gray-900 disabled:opacity-50"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              disabled={!selectedCategoryId}
            >
              <option value="">-- Elegir equipo --</option>
              {teams.map((team) => (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </div>

          {/* Configuración visual */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Superior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setTopLogo, "exportTopLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Imagen de Fondo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBgImage, "exportBgImage")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={() => {
                  setBgImage(DEFAULT_BG);
                  localStorage.removeItem("exportBgImage");
                }}
                className="mt-2 text-xs text-red-500 hover:underline"
              >
                Restaurar fondo por defecto
              </button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Inferior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBottomLogo, "exportBottomLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <button
              onClick={handleExport}
              disabled={isExporting || !selectedTeam}
              className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${
                isExporting || !selectedTeam
                  ? "bg-yellow-400"
                  : "bg-yellow-500 hover:bg-yellow-600"
              }`}
            >
              {isExporting ? "Generando..." : "Descargar Imagen"}
            </button>
          </div>
        </div>

        {/* Vista previa */}
        <div className="w-full md:w-2/3 p-6 bg-gray-200 overflow-auto flex items-center justify-center">
          <div
            ref={exportRef}
            className="relative bg-blue-900 text-white overflow-hidden shadow-xl flex flex-col items-center justify-center"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              fontFamily: "'Inter', sans-serif",
              width: 500,
              minHeight: 500,
              padding: "40px 20px",
            }}
          >
            {/* Cabecera */}
            <div className="flex flex-col items-center mb-8">
              {topLogo && (
                <img
                  src={topLogo}
                  alt="Top Logo"
                  className="h-16 object-contain mb-4"
                />
              )}
              <h1
                className="text-5xl font-black tracking-tighter uppercase m-0 leading-none text-center"
                style={{ fontFamily: "Impact, sans-serif" }}
              >
                CAMPEÓN
              </h1>
              {subtitle && (
                <h2 className="text-lg font-bold tracking-widest uppercase mt-2 text-blue-200 text-center">
                  {subtitle}
                </h2>
              )}
            </div>

            {/* Contenido central */}
            {selectedTeam ? (
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  {selectedTeam.picture_url ? (
                    <img
                      src={selectedTeam.picture_url}
                      alt={selectedTeam.team_name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-yellow-400 shadow-lg"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-white/20 border-4 border-yellow-400 flex items-center justify-center">
                      <FaShieldAlt size={60} className="text-white/70" />
                    </div>
                  )}
                  <FaCrown className="absolute -top-4 -right-4 text-yellow-400 text-4xl drop-shadow-lg" />
                </div>
                <h3 className="text-3xl font-black uppercase tracking-wider text-yellow-300 drop-shadow-md">
                  {selectedTeam.team_name}
                </h3>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 text-white/50">
                <FaCrown size={80} className="mb-4" />
                <p className="text-xl">Selecciona un equipo campeón</p>
              </div>
            )}

            {/* Pie */}
            <div className="mt-auto pt-8">
              {bottomLogo && (
                <img
                  src={bottomLogo}
                  alt="Bottom Logo"
                  className="h-14 object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportChampionModal;
